import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { SpoInspectedColumn } from "@/api/admin/libraries_indexing"

import { Columns3, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"

import {
  type ExtraDataRow,
  extraDataTypeLabelKey,
  inspectedColumnPresent,
} from "./extra-data-model"

type ExtraDataColumnPickerProps = {
  columns: SpoInspectedColumn[]
  listLabel: string
  rows: ExtraDataRow[]
  onAdd: (columns: SpoInspectedColumn[]) => void
  onClose: () => void
}

/**
 * The columns Graph reported for the target list, each with the extra-data type and path it
 * maps to. Columns already present as rows are shown checked and locked; hidden and system
 * columns stay out of the way until asked for.
 */
export function ExtraDataColumnPicker({
  columns,
  listLabel,
  rows,
  onAdd,
  onClose,
}: ExtraDataColumnPickerProps) {
  const { t } = useTranslation()
  const [showHidden, setShowHidden] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())

  const visible = useMemo(
    () => columns.filter((column) => showHidden || !(column.hidden || column.system)),
    [columns, showHidden]
  )
  const addable = visible.filter((column) => !inspectedColumnPresent(rows, column))
  const chosen = addable.filter((column) => selected.has(column.name))
  const allChosen = addable.length > 0 && chosen.length === addable.length

  const toggle = (name: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (on) next.add(name)
      else next.delete(name)
      return next
    })

  const typeLabel = (type: string) => {
    const key = extraDataTypeLabelKey(type)
    return key ? t(`adminDashboard.libraries.editor.extraData.types.${key}`, type) : type
  }

  return (
    <div className="bg-muted/30 space-y-3 rounded-md border p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-2 text-sm font-medium">
            <Columns3 className="size-4" />
            {t("adminDashboard.libraries.editor.extraData.columns.title", {
              defaultValue: "Columns in {{list}}",
              list: listLabel,
            })}
          </p>
          <p className="text-muted-foreground text-xs">
            {t(
              "adminDashboard.libraries.editor.extraData.columns.help",
              "Pick the columns to index as extra data. Path and type follow from the column."
            )}
          </p>
        </div>
        <Button
          aria-label={t("adminDashboard.libraries.editor.extraData.columns.close", "Close")}
          className="size-7"
          size="icon"
          type="button"
          variant="ghost"
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={allChosen}
            disabled={addable.length === 0}
            id="extra-data-columns-all"
            onCheckedChange={(value) =>
              setSelected(value === true ? new Set(addable.map((c) => c.name)) : new Set())
            }
          />
          <Label className="text-xs font-normal" htmlFor="extra-data-columns-all">
            {t("adminDashboard.libraries.editor.extraData.columns.selectAll", "Select all visible")}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={showHidden}
            id="extra-data-columns-hidden"
            onCheckedChange={(value) => setShowHidden(value === true)}
          />
          <Label className="text-xs font-normal" htmlFor="extra-data-columns-hidden">
            {t(
              "adminDashboard.libraries.editor.extraData.columns.showHidden",
              "Show hidden columns"
            )}
          </Label>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.libraries.editor.extraData.columns.empty", "No columns available")}
        </p>
      ) : (
        <ul className="max-h-64 space-y-1 overflow-y-auto pr-1">
          {visible.map((column) => {
            const present = inspectedColumnPresent(rows, column)
            const id = `extra-data-column-${column.name}`
            return (
              <li key={column.name} className="flex items-start gap-2 rounded px-1 py-1">
                <Checkbox
                  checked={present || selected.has(column.name)}
                  className="mt-0.5"
                  disabled={present}
                  id={id}
                  onCheckedChange={(value) => toggle(column.name, value === true)}
                />
                <Label
                  className="flex flex-1 cursor-pointer flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-normal"
                  htmlFor={id}
                >
                  <span className="font-medium">{column.display_name || column.name}</span>
                  <code className="text-muted-foreground text-[11px]">{column.suggested.path}</code>
                  <Badge className="text-[10px]" variant="outline">
                    {column.kind}
                    {column.multi ? " ×n" : ""}
                  </Badge>
                  <span className="text-muted-foreground">
                    {t("adminDashboard.libraries.editor.extraData.columns.mapped", {
                      defaultValue: "as {{type}}",
                      type: typeLabel(column.suggested.type),
                    })}
                  </span>
                  {present && (
                    <Badge className="text-[10px]" variant="secondary">
                      {t(
                        "adminDashboard.libraries.editor.extraData.columns.present",
                        "Already added"
                      )}
                    </Badge>
                  )}
                </Label>
              </li>
            )
          })}
        </ul>
      )}

      <div className="flex justify-end gap-2">
        <Button size="sm" type="button" variant="ghost" onClick={onClose}>
          {t("adminDashboard.libraries.editor.extraData.columns.close", "Close")}
        </Button>
        <Button
          disabled={chosen.length === 0}
          size="sm"
          type="button"
          onClick={() => onAdd(chosen)}
        >
          {t("adminDashboard.libraries.editor.extraData.columns.addSelected", {
            defaultValue: "Add selected ({{count}})",
            count: chosen.length,
          })}
        </Button>
      </div>
    </div>
  )
}
